import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components/macro'
import {
  border,
  BorderProps,
  color,
  compose,
  flexbox,
  FlexboxProps,
  grid,
  GridProps,
  layout,
  LayoutProps,
  space,
  SpaceProps,
  textStyle,
  TextStyleProps,
  typography,
  TypographyProps,
} from 'styled-system'
import { themeGet } from '@styled-system/theme-get'
import isString from 'lodash/isString'
import FileSaver from 'file-saver'
import ReactMde from 'react-mde'
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx'
import palette from 'src/designSystem/variables/palette'
import { DownloadArrowSVG, ExpandSVG } from 'src/components/Icons/Basic'
import {
  Tooltip,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { $truncate } from 'src/globalStyles'
import { converter, noLinkConverter } from '../../../Utils/ShowDownConverter'

function buildCsv(tableEl: HTMLTableElement): string {
  const rows: string[][] = []
  const headers = Array.from(tableEl.querySelectorAll('thead th')).map(
    (th) => th.textContent ?? '',
  )
  if (headers.length > 0) rows.push(headers)
  tableEl.querySelectorAll('tbody tr').forEach((tr) => {
    rows.push(
      Array.from(tr.querySelectorAll('td')).map((td) => td.textContent ?? ''),
    )
  })
  return rows
    .map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(','))
    .join('\n')
}

function countTableColumns(children: React.ReactNode): number {
  let count = 0
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child) || child.type !== 'thead') return
    React.Children.forEach(
      (child.props as { children: React.ReactNode }).children,
      (row) => {
        if (!React.isValidElement(row)) return
        count = React.Children.count(
          (row.props as { children: React.ReactNode }).children,
        )
      },
    )
  })
  return count
}

function countTableRows(children: React.ReactNode): number {
  let count = 0
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child) || child.type !== 'tbody') return
    count = React.Children.count(
      (child.props as { children: React.ReactNode }).children,
    )
  })
  return count
}

export function useTableExpand(): {
  tableRef: React.RefObject<HTMLTableElement>
  isExpanded: boolean
  toggle: () => void
  collapse: () => void
  boundingRect: DOMRect | null
} {
  const tableRef = useRef<HTMLTableElement>(null)
  const [isExpanded, setIsExpanded] = useState(false)
  const [boundingRect, setBoundingRect] = useState<DOMRect | null>(null)

  const toggle = useCallback(() => {
    if (!isExpanded && tableRef.current) {
      setBoundingRect(tableRef.current.getBoundingClientRect())
    }
    setIsExpanded((prev) => !prev)
  }, [isExpanded])

  const collapse = useCallback(() => setIsExpanded(false), [])

  useEffect(() => {
    if (!isExpanded) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') collapse()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isExpanded, collapse])

  return { tableRef, isExpanded, toggle, collapse, boundingRect }
}

const TableHeaderBarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2px 0 4px 0;
`

const ColumnCountLabel = styled.span`
  font-style: italic;
  font-size: 11px;
  color: ${palette.neutral[400]};
  line-height: 1;
`

const TableIconButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const TableActionLabel = styled.span`
  font-size: 11px;
  color: ${palette.neutral[600]};
  line-height: 1;
`

const TableActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  &:hover {
    background: ${palette.neutral[30]};
  }
`

const SmallIconWrapper = styled.span`
  display: inline-flex;
  svg {
    width: 11px;
    height: 11px;
  }
`

export const TableHeaderBar = ({
  columnCount,
  rowCount,
  isExpanded,
  onDownloadCsv,
  onToggleExpand,
}: {
  columnCount: number
  rowCount: number
  isExpanded: boolean
  onDownloadCsv: () => void
  onToggleExpand: () => void
}): JSX.Element | null => {
  if (columnCount < 2) return null
  return (
    <TableHeaderBarContainer>
      <TableIconButtonGroup>
        <ColumnCountLabel>
          {columnCount} columns · {rowCount} rows
        </ColumnCountLabel>
        <Tooltip
          id="table-expand-btn"
          tooltipBody={isExpanded ? 'Collapse table' : 'Expand table'}
          placement={TooltipPositions.top}
        >
          <TableActionButton
            data-testid="expand-table-btn"
            type="button"
            onClick={onToggleExpand}
          >
            <SmallIconWrapper>
              <ExpandSVG />
            </SmallIconWrapper>
            <TableActionLabel>Expand</TableActionLabel>
          </TableActionButton>
        </Tooltip>
      </TableIconButtonGroup>
      <Tooltip
        id="table-download-btn"
        tooltipBody="Download CSV"
        placement={TooltipPositions.top}
      >
        <TableActionButton type="button" onClick={onDownloadCsv}>
          <SmallIconWrapper>
            <DownloadArrowSVG />
          </SmallIconWrapper>
          <TableActionLabel>Download</TableActionLabel>
        </TableActionButton>
      </Tooltip>
    </TableHeaderBarContainer>
  )
}

const OverlayBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1040;
  pointer-events: none;
`

const OverlayCard = styled.div<{
  $top: number
  $left: number
  $width: number
  $height: number
  $animating: boolean
}>`
  position: fixed;
  z-index: 1041;
  background: white;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  transition: top 0.28s ease, left 0.28s ease, width 0.28s ease,
    height 0.28s ease;

  top: ${({ $animating, $top }) => ($animating ? '10vh' : `${$top}px`)};
  left: ${({ $animating, $left }) => ($animating ? '5vw' : `${$left}px`)};
  width: ${({ $animating, $width }) => ($animating ? '90vw' : `${$width}px`)};
  max-width: 90vw;
  height: ${({ $animating, $height }) =>
    $animating ? '80vh' : `${$height}px`};
`

const OverlayHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 6px 8px 0;
  flex-shrink: 0;
`

const OverlayContent = styled.div`
  overflow: auto;
  flex: 1;
  padding: 0 16px 16px;
  display: flex;
  /* 'safe' so a table wider than the overlay start-aligns (and stays fully
     scrollable) instead of centering and clipping its first column off-screen. */
  justify-content: safe center;
  align-items: flex-start;
`

const OverlayCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: ${palette.neutral[600]};
  padding: 4px 8px;
  border-radius: 4px;
  &:hover {
    background: ${palette.neutral[30]};
  }
`

export const ExpandedTableOverlay = ({
  boundingRect,
  onClose,
  children,
}: {
  boundingRect: DOMRect
  onClose: () => void
  children: React.ReactNode
}): JSX.Element => {
  const [isAnimating, setIsAnimating] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsAnimating(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [onClose])

  return ReactDOM.createPortal(
    <>
      <OverlayBackdrop />
      <OverlayCard
        ref={cardRef}
        data-testid="expanded-table-overlay"
        $top={boundingRect.top}
        $left={boundingRect.left}
        $width={boundingRect.width}
        $height={boundingRect.height}
        $animating={isAnimating}
      >
        <OverlayHeader>
          <OverlayCloseButton
            onClick={onClose}
            aria-label="close expanded table"
          >
            ✕
          </OverlayCloseButton>
        </OverlayHeader>
        <OverlayContent>{children}</OverlayContent>
      </OverlayCard>
    </>,
    document.body,
  )
}

type MarkupSectionProps = LayoutProps &
  GridProps &
  FlexboxProps &
  SpaceProps &
  BorderProps &
  TypographyProps &
  TextStyleProps & {
    color?: string
    bg?: string
    style?: React.CSSProperties
    isLarge?: boolean
    isEvidence?: boolean
    truncateWithEllipse?: boolean
  }

export type MarkupProps = {
  rawHtml?: string
  markdown?: string
  sectionProps?: MarkupSectionProps
  isEvidence?: boolean
  isLarge?: boolean
  truncate?: number
  truncateWithEllipse?: boolean
  isHtml?: boolean
  customConverter?: showdown.Converter
}

/**
 * Markup component
 * @param rawHtml - raw html string
 * @param markdown - markdown string
 * @param sectionProps - styled-system props
 * @param isEvidence - boolean to determine if the markup is for evidence. If true, will not render links. The reason we don't render links for evidence is because it causes an error from showdown.js.
 */

export const Markup = ({
  rawHtml,
  markdown,
  sectionProps,
  isEvidence,
  isLarge,
  truncate,
  truncateWithEllipse,
  isHtml,
  customConverter,
}: MarkupProps): JSX.Element => {
  let html
  if (rawHtml) {
    html = rawHtml
  } else if (isString(markdown)) {
    html = isEvidence
      ? noLinkConverter.makeHtml(markdown)
      : customConverter
      ? customConverter.makeHtml(markdown)
      : converter.makeHtml(markdown)
  } else {
    html = ''
  }

  if (truncate) {
    html = html
      .split('\n')
      .slice(0, truncate + 1)
      .join('\n')
  }

  return isHtml ? (
    <HTMLMarkupSection
      {...sectionProps}
      isLarge={isLarge}
      isEvidence={isEvidence}
      dangerouslySetInnerHTML={{ __html: html }} // nosemgrep
    />
  ) : (
    <MarkupSection
      {...sectionProps}
      isLarge={isLarge}
      isEvidence={isEvidence}
      dangerouslySetInnerHTML={{ __html: html }} // nosemgrep
      truncateWithEllipse={truncateWithEllipse}
    />
  )
}

export const MarkupSection = styled.section.attrs<MarkupSectionProps>({
  className: 'markup-section',
})<MarkupSectionProps>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: Poppins;
  font-size: ${({ isLarge }) => (isLarge ? '18px' : '14px')};
  font-weight: initial;
  letter-spacing: -0.03;
  margin: 0;
  margin-bottom: ${(props) => `${themeGet('space.xs')(props)}px`};
  padding: 0;
  ${({ truncateWithEllipse }) =>
    truncateWithEllipse &&
    `
    ${$truncate}
  `}

  ${({ isEvidence }) =>
    isEvidence
      ? compose(layout, grid, flexbox, border, color, typography, textStyle)
      : compose(
          space,
          layout,
          grid,
          flexbox,
          border,
          color,
          typography,
          textStyle,
        )}

  ${({ isEvidence }) =>
    isEvidence &&
    `padding: 0 12px;
  table {
    position: relative;
  }
  thead {
    background: white;
    white-space: nowrap;
    position: sticky;
    top: 0;
    th {
      padding: 12px;
      tr {
      }
    }
  }

  td {
    vertical-align: top;
    padding: 12px;
    width: 100%;
  }
  `}

  > * {
    word-break: break-word;
  }

  tbody > tr:nth-child(odd) {
    background-color: ${themeGet('colors.bg.neutral')};
  }

  > h1,
  > h2,
  > h3,
  > h4,
  > h5,
  > h6,
  > ul,
  > ol,
  > p,
  > span {
    :only-child {
      margin: 0 !important;
    }
  }

  h1 {
    font-family: Poppins;
    font-weight: 500;
    font-size: 18px;
    letter-spacing: -0.5;
    margin-bottom: ${(props) => `${themeGet('space.xs')(props)}px`};
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: Poppins;
    font-weight: 500;
    font-size: 14px;
    letter-spacing: -0.4;
    margin-bottom: ${(props) => `${themeGet('space.xs')(props)}px`};
  }

  table:not(:last-child) {
    margin-bottom: ${(props) => `${themeGet('space.xl')(props)}px`};
  }

  ul,
  ol {
    font-family: Poppins;
    font-size: 14px;
    letter-spacing: -0.03;
    line-height: 22px;
    margin: 0;
    margin-top: ${(props) => `${themeGet('space.xs')(props)}px`};
    margin-bottom: ${(props) => `${themeGet('space.xs')(props)}px`};
    margin-left: ${(props) => `${themeGet('space.xs')(props)}px`};
  }

  a,
  p,
  span {
    margin-left: 8px;
  }

  a {
    font-weight: 500;
    font-size: 14px;
    letter-spacing: -0.03;
    text-decoration: underline;
  }

  code {
    border: 1px solid;
    border-color: ${themeGet('colors.border.neutral.light')};
    border-radius: 6px;
    background-color: ${themeGet('colors.bg.neutral')};
    color: ${themeGet('colors.text.default')};
    font-family: Courier;
    font-size: 85%;
    letter-spacing: -0.03px;
    line-height: 22px;
    padding: 0.2em 0.4em;
    margin: 0;
  }

  pre > code {
    word-break: normal;
    white-space: pre;
    background: transparent;
    font-size: 100%;
    padding: 0;
    margin: 0;
  }

  pre code {
    display: inline;
    padding: 0;
    margin: 0;
    overflow: visible;
    line-height: inherit;
    background-color: ${themeGet('colors.bg.neutral')};
    border: 0;
    max-width: auto;
    word-wrap: normal;
  }

  pre {
    word-wrap: normal;
    padding: 16px;
    font-size: 85%;
    line-height: 1.45;
    background-color: ${themeGet('colors.bg.neutral')};
    border-radius: 6px;
    overflow: visible;
  }
`

export const HTMLMarkupSection = styled(MarkupSection).attrs({
  className: 'html-markup-section',
})<MarkupSectionProps>`
  overflow: auto;
  padding: 20px;
  border-radius: 14px;
  border: 1px solid ${palette.neutral[100]};
  table,
  th,
  td {
    border: 1px solid ${palette.neutral[100]};
  }
`

const StyledMarkdown = styled(Markdown)`
  h4 {
    font-size: 14px;
    margin-top: 12px;
    font-weight: 600;
  }

  h3 {
    font-size: 16px;
    margin-top: 12px;
    font-weight: 600;
  }

  h2 {
    font-size: 18px;
    margin-top: 12px;
    font-weight: 600;
  }

  h1 {
    font-size: 20px;
    margin-top: 12px;
    font-weight: 600;
  }
`

const TableWrapper = styled.div`
  overflow-x: auto; /* horizontal scroll if table is wider than container */
`

const StyledTable = styled.table`
  border-collapse: collapse;
  width: max-content;
  table-layout: auto;
  font-size: 0.95rem;

  th,
  td {
    padding: 0.75em 1em;
    border-bottom: 1px solid ${palette.neutral[100]};
    vertical-align: top;

    /* Wrap long words, short text stays on one line */
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    min-width: 100px;
    max-width: 520px;
  }

  thead {
    background: ${palette.neutral[30]};
    text-align: left;
  }
`

const StyledH1 = styled.h1`
  font-size: 20px;
  margin-top: 12px;
  font-weight: 600;
`

export const TableOverride = (
  props: React.TableHTMLAttributes<HTMLTableElement>,
): JSX.Element => {
  const { tableRef, isExpanded, toggle, collapse, boundingRect } =
    useTableExpand()
  const columnCount = countTableColumns(props.children)
  const rowCount = countTableRows(props.children)

  const handleDownload = useCallback(() => {
    if (!tableRef.current) return
    const csv = buildCsv(tableRef.current)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    FileSaver.saveAs(blob, 'table.csv')
  }, [tableRef])

  return (
    <>
      <TableHeaderBar
        columnCount={columnCount}
        rowCount={rowCount}
        isExpanded={isExpanded}
        onDownloadCsv={handleDownload}
        onToggleExpand={toggle}
      />
      <TableWrapper>
        <StyledTable ref={tableRef} {...props} />
      </TableWrapper>
      {isExpanded && boundingRect && (
        <ExpandedTableOverlay boundingRect={boundingRect} onClose={collapse}>
          <StyledTable {...props} />
        </ExpandedTableOverlay>
      )}
    </>
  )
}

export const MarkdownViewer = ({
  markdown,
  overrides,
  className,
  disableParsingRawHTML = false,
  forceBlock = false,
}: {
  markdown: string
  overrides?: MarkdownToJSX.Overrides
  className?: string
  disableParsingRawHTML?: boolean
  // Force block-level rendering so a single-paragraph answer is wrapped in
  // <p> (markdown-to-jsx renders lone paragraphs inline otherwise). Needed
  // for the `p` override to fire — e.g. the chat's post-paragraph rich
  // object expansion. Opt-in so other callers keep the inline behavior.
  forceBlock?: boolean
}): JSX.Element => {
  return (
    <StyledMarkdown
      className={className}
      options={{
        disableParsingRawHTML,
        forceBlock,
        overrides: { table: TableOverride, h1: StyledH1, ...overrides },
      }}
    >
      {markdown}
    </StyledMarkdown>
  )
}

export const MdEditor = ({
  value,
  onChange,
  minEditorHeight,
}: {
  value: string
  onChange: (v: string) => void
  minEditorHeight?: number
}): JSX.Element => {
  const [selectedTab, setSelectedTab] = useState<'write' | 'preview'>('write')

  return (
    <ReactMde
      value={value}
      onChange={onChange}
      selectedTab={selectedTab}
      onTabChange={setSelectedTab}
      minEditorHeight={minEditorHeight}
      generateMarkdownPreview={(markdown) =>
        Promise.resolve(converter.makeHtml(markdown))
      }
    />
  )
}
